import * as React from 'react';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';
import { ActionButtonDefinition, PosScreenClassComponent, ScreenSize, ThemeManager } from 'pos/ui';
import { DocumentPaymentDTO, Res, TicketDTO } from 'core/api';
import { Cols, Col, Flex } from 'core/ui';
import { Modal } from 'core/ui-next/modal';

type Props = {
	reloadTicket: () => Promise<TicketDTO>,
	initialTicket: TicketDTO,
	addPayment: (pendingAmount: number) => Promise<Result>;
	removePayment: (paidAmount: number, tipAmount: number) => Promise<Result>;
	refundPayment: (payment: DocumentPaymentDTO) => Promise<Result>;
	documentType: 'SalesOrder' | 'DeliveryNote' | 'Ticket';
	printDocument: () => Promise<void>;
} & Modal.Actions<void>;

type State = { ticket: TicketDTO, selectedPayment: Nullable<DocumentPaymentDTO> };

export default class ShowPaymentsScreen extends PosScreenClassComponent<Props, State> {

	createInitialState(props: Props): Nullable<State> {
		return { ticket: props.initialTicket, selectedPayment: null };
	}

	onComponentDidMount(): void {
		const getFailureEvent = () => {
			switch (this.props.documentType) {
				case 'DeliveryNote': return 'pos:delivery-note:operation-failed';
				case 'SalesOrder': return 'pos:sales-order:operation-failed';
				case 'Ticket': return 'pos:sale:operation-failed';
			}
		};

		this.on(getFailureEvent(), async () => this.cancel());
	}

	onComponentWillUnmount(): void { }

	protected getSize(): ScreenSize {
		return 'medium';
	}

	protected getScreenId(): string {
		return 'SHOW_PAYMENTS';
	}

	protected getTitle(): string {
		return Res.Web.SHOW_PAYMENTS();
	}

	protected getImage(): string | SvgIcon {
		return Images.ShowPayments;
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {

		const refundButton = this.state.selectedPayment && this.state.selectedPayment.Amount > 0 ? { title: Res.Web.SHOW_PAYMENTS_REFUND_PAYMENT_BTN(), image: Images.RefundPayment, action: this.refundPayment } : null;
		return [
			this.cancelButton(),
			null,
			refundButton,
			null,
			{ title: Res.Web.SHOW_PAYMENTS_REMOVE_PAYMENT_BTN(), image: Images.RemovePayment, action: this.removePayment },
			{ title: Res.Web.SHOW_PAYMENTS_ADD_PAYMENT_BTN(), image: Images.AddPayment, action: this.addPayment },
		];
	}

	protected useKeypad() {
		return false;
	}

	addPayment = async (): Promise<void> => {
		const result = await this.props.addPayment(this.state.ticket.PendingAmount);
		await this.updateTicket();
		if (result.ok)
			await this.props.printDocument();
	};

	removePayment = async (): Promise<void> => {
		const result = await this.props.removePayment(this.state.ticket.PrepaidAmount, this.state.ticket.TipAmount);
		await this.updateTicket();
		if (result.ok)
			await this.props.printDocument();
	};

	refundPayment = async (): Promise<void> => {

		if (this.state.selectedPayment === null)
			return;

		const result = await this.props.refundPayment(this.state.selectedPayment);
		await this.updateTicket();
		if (result.ok)
			await this.props.printDocument();
	};

	private updateTicket = async (): Promise<void> => {
		const ticket = await this.props.reloadTicket();
		this.setState({ ticket });
	};

	protected getContent(): React.ReactNode {

		const BigAmount = ({ label, amount }: { label: string, amount: number }) => <div className='p-xs'>
			{label}<br /><span className='text-26 font-bold'>{UI.Format.asMoney(amount)}</span>
		</div>;

		const payments = this.state.ticket.DocumentPayments.map((dp, index) => {
			return <li className={dp.Id === this.state.selectedPayment?.Id ? 'selected' : ''} key={index} onClick={() => this.setState({ selectedPayment: dp })}>
				<Cols>
					<Col width='12ex'>{UI.Format.asDate(dp.Date)}</Col>
					<Col width='12ex' className='text-center'>{UI.Format.asMoney(dp.Amount)}</Col>
					<Col width='*' className='m-l-xs'>{dp.Method}</Col>
					{ThemeManager.isLargeDevice() ? <Col width='*'>{dp.ExtraInformation}</Col> : null}
					{this.state.ticket.TipAmount !== 0 ? <Col width='8ex' className='m-l-xs'>{UI.Format.asMoney(dp.TipAmount)}</Col> : null}
				</Cols>
			</li>;
		});

		return <Flex.Vertical>
			<Flex.Item>
				<Flex.Horizontal>
					<Flex.Item size='*' className='text-center' style={{ margin: 'auto' }}>
						<BigAmount label={Res.Web.SHOW_PAYMENTS_TOTAL_LBL()} amount={this.state.ticket.NetAmount} />
					</Flex.Item>
					<Flex.Item size='*' className='text-center' style={{ margin: 'auto' }}>
						<BigAmount label={Res.Web.SHOW_PAYMENTS_PREPAID_LBL()} amount={this.state.ticket.PrepaidAmount} />
					</Flex.Item>
					{
						ThemeManager.isLargeDevice()
							? <Flex.Item size='*' className='text-center' style={{ margin: 'auto' }}>
								<BigAmount label={Res.Web.SHOW_PAYMENTS_PENDING_LBL()} amount={this.state.ticket.PendingAmount} />
							</Flex.Item>
							: null
					}
					{
						ThemeManager.isLargeDevice() && this.state.ticket.TipAmount !== 0
							? <Flex.Item size='*' className='text-center' style={{ margin: 'auto' }} >
								<BigAmount label={Res.Web.SHOW_PAYMENTS_TIP_LBL()} amount={this.state.ticket.TipAmount} />
							</Flex.Item>
							: null

					}
				</Flex.Horizontal>
			</Flex.Item>
			<Flex.Item>
				<Flex.Horizontal>
					{
						!ThemeManager.isLargeDevice() && this.state.ticket.TipAmount !== 0
							? <Flex.Item size='*' className='text-center' style={{ margin: 'auto' }} >
								<BigAmount label={Res.Web.SHOW_PAYMENTS_TIP_LBL()} amount={this.state.ticket.TipAmount} />
							</Flex.Item>
							: null
					}
					{
						!ThemeManager.isLargeDevice()
							? <Flex.Item size='*' className='text-center' style={{ margin: 'auto' }} >
								<BigAmount label={Res.Web.SHOW_PAYMENTS_PENDING_LBL()} amount={this.state.ticket.PendingAmount} />
							</Flex.Item>
							: null
					}
				</Flex.Horizontal>
			</Flex.Item>

			<Flex.Item>
				<Cols className='list-header'>
					<Col width='12ex'>{Res.Web.SHOW_PAYMENTS_PAYMENT_CREATION_DATE_CLM()}</Col>
					<Col width='12ex'>{Res.Web.SHOW_PAYMENTS_PAYMENT_PAID_AMOUNT_CLM()}</Col>
					<Col width='*'>{Res.Web.SHOW_PAYMENTS_PAYMENT_METHOD_CLM()}</Col>
					{ThemeManager.isLargeDevice() ? <Col width='*'>{Res.Web.SHOW_PAYMENTS_PAYMENT_INFORMATION_CLM()}</Col> : null}
					{this.state.ticket.TipAmount !== 0 ? <Col width='8ex'>{Res.Web.SHOW_PAYMENTS_PAYMENT_TIP_CLM()}</Col> : null}
				</Cols>
			</Flex.Item>
			<Flex.Item size='*'>
				<ul className='list'>
					{payments}
				</ul>
			</Flex.Item>
		</Flex.Vertical>;
	}
}


